import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { VehicleList } from "../components/vehicle-list";
import { MapViewSection } from "../components/map-view-section";
import { useGpsTrackingData } from "../model/useGpsTrackingData";
import { GpsTrackingPoint, ViewMode, TimelineSource } from "../model/types";
import { parseISO } from "date-fns";
import { getShiftWindow } from "../lib/utils";
import {
  findPositionAtTime,
  gpsPointsToTimelinePoints,
  createRouteTimelineSegments,
} from "@racoon/ui";
import { useT, Namespace } from "@racoon/i18n";
import { photoPointsForJobs } from "~/features/map-view/model/helpers/photo-points";

export default function GpsTrackingPage() {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>();
  const [viewMode, setViewMode] = useState<ViewMode>("map");
  const [selectedPointId, setSelectedPointId] = useState<string>();
  const [selectedShiftId, setSelectedShiftId] = useState<string>();
  const [playbackTime, setPlaybackTime] = useState<number | null>(null);
  const [isPlaybackActive, setIsPlaybackActive] = useState(false);
  const [timelineSource, setTimelineSource] = useState<TimelineSource>("all");
  const [initialPlaybackTime, setInitialPlaybackTime] = useState<number | null>(null);

  // Track if the current selection is from URL pre-selection
  const isFromUrlPreSelection = useRef(false);

  const {
    vehicles,
    gpsPoints,
    selectedVehicle,
    usedPickupLocations,
    stepPins,
    usedDepots,
    routes,
    start,
    end,
    setDateRange,
  } = useGpsTrackingData({
    searchQuery,
    selectedVehicleId,
  });

  const startDate = useMemo(() => parseISO(start), [start]);
  const endDate = useMemo(() => parseISO(end), [end]);

  const handlePointClick = useCallback(
    (point: GpsTrackingPoint) => {
      setSelectedPointId(point.id);
      if (selectedVehicleId !== point.vehicleId) {
        setSelectedVehicleId(point.vehicleId);
        setSelectedShiftId(undefined);
      }
    },
    [selectedVehicleId],
  );

  const handleVehicleSelect = useCallback(
    (vehicleId: string) => {
      const isTogglingOff = selectedVehicleId === vehicleId;
      setSelectedVehicleId(isTogglingOff ? undefined : vehicleId);
      setSelectedShiftId(undefined);

      // This is a manual selection, not from URL
      isFromUrlPreSelection.current = false;

      // Clear URL parameters when manually changing vehicle
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("shiftId");
        newParams.delete("truckId");
        newParams.delete("startTime");
        return newParams;
      });
    },
    [selectedVehicleId, setSearchParams],
  );

  const handleShiftSelect = useCallback(
    (vehicleId: string, shiftId: string) => {
      const isSameSelection = selectedShiftId === shiftId && selectedVehicleId === vehicleId;
      // A manual pick; a deep link whose shift wasn't found must not keep blocking the reset.
      isFromUrlPreSelection.current = false;
      setSelectedVehicleId(vehicleId);
      setSelectedShiftId(isSameSelection ? undefined : shiftId);
    },
    [selectedShiftId, selectedVehicleId],
  );

  const selectedShift = useMemo(
    () => selectedVehicle?.shifts.find((s) => String(s.id) === selectedShiftId) ?? null,
    [selectedVehicle, selectedShiftId],
  );

  const filteredGpsPoints = useMemo(() => {
    if (!selectedShift) return gpsPoints;

    // An unfinished shift shows every point after its start.
    const { from, to } = getShiftWindow(selectedShift);
    return gpsPoints.filter(
      (point: GpsTrackingPoint) =>
        (!from || point.timestamp >= from) && (!to || point.timestamp <= to),
    );
  }, [gpsPoints, selectedShift]);

  // The time span of the whole track, deliberately independent of playback.
  //
  // `displayGpsPoints` below is truncated at `playbackTime`, so deriving a
  // fetch window from it would move the window on every playback tick. Anything
  // keyed on that window (the GPS-stops query) would then refetch per tick, and
  // with an RTK Query cache key that changes each time, each tick becomes its
  // own cache entry rather than a hit. Fetch once for the full span and let the
  // consumer reveal things progressively.
  const trackWindow = useMemo(() => {
    if (filteredGpsPoints.length === 0) return { from: null, to: null };

    let min = filteredGpsPoints[0].timestamp.getTime();
    let max = min;
    for (const point of filteredGpsPoints) {
      const time = point.timestamp.getTime();
      if (time < min) min = time;
      if (time > max) max = time;
    }

    return { from: new Date(min), to: new Date(max) };
  }, [filteredGpsPoints]);

  // The API returns every route of the truck; a picked shift has to narrow
  // them too, otherwise the table lists the other shifts' work.
  const shiftRoutes = useMemo(() => {
    if (!selectedShift) return routes;

    return routes.filter((route) => route.driverOnShift?.id === selectedShift.id);
  }, [routes, selectedShift]);

  const photoPoints = useMemo(
    () => photoPointsForJobs(shiftRoutes.flatMap((route) => route.jobs ?? [])),
    [shiftRoutes],
  );

  // Timeline player data
  const timelinePoints = useMemo(() => {
    return gpsPointsToTimelinePoints(filteredGpsPoints, shiftRoutes);
  }, [filteredGpsPoints, shiftRoutes]);

  // Handle route pre-selection from URL parameters
  useEffect(() => {
    const shiftId = searchParams.get("shiftId");
    const truckId = searchParams.get("truckId");
    const startTimeParam = searchParams.get("startTime");

    // Only proceed if we have shiftId and truckId and haven't already pre-selected
    if (!shiftId || !truckId) {
      return;
    }

    if (selectedVehicleId) {
      return;
    }

    // Wait for vehicles data to be loaded
    if (vehicles.length === 0) {
      return;
    }

    // Check if this vehicle exists in our vehicles list
    const vehicle = vehicles.find((v) => String(v.id) === truckId);

    if (!vehicle) {
      return;
    }

    // Mark that this selection is from URL pre-selection
    isFromUrlPreSelection.current = true;

    setSelectedVehicleId(truckId);

    if (vehicle.shifts.some((s) => String(s.id) === shiftId)) {
      setSelectedShiftId(shiftId);
    }

    // Only park the player's slider there: starting playback would cut the
    // track at that moment, unlike picking the shift by hand.
    if (startTimeParam && !initialPlaybackTime) {
      const startTime = Number(startTimeParam);
      if (!isNaN(startTime)) {
        setInitialPlaybackTime(startTime);
      }
    }
  }, [searchParams, vehicles, selectedVehicleId, initialPlaybackTime]);

  // Reset playback when vehicle or shift changes (but not from URL pre-selection)
  useEffect(() => {
    // Skip reset if this change is from URL pre-selection
    if (isFromUrlPreSelection.current) {
      // Don't reset the flag here - it will be reset after both vehicle and driver are set
      return;
    }

    setPlaybackTime(null);
    setIsPlaybackActive(false);
    setInitialPlaybackTime(null);
  }, [selectedVehicleId, selectedShiftId]);

  // Reset the URL pre-selection flag after both vehicle and shift are set
  useEffect(() => {
    if (isFromUrlPreSelection.current && selectedVehicleId && selectedShiftId) {
      isFromUrlPreSelection.current = false;
    }
  }, [selectedVehicleId, selectedShiftId]);

  // Fact-based segments drive the map playback timeline; the table can also
  // show the GPS-derived ones, depending on the selected source.
  const factSegments = useMemo(
    () =>
      createRouteTimelineSegments(
        shiftRoutes,
        timelinePoints,
        (key, options) => t(Namespace.GpsTracking, key as any, options),
        "fact",
      ),
    [shiftRoutes, timelinePoints, t],
  );

  const gpsSegments = useMemo(
    () =>
      createRouteTimelineSegments(
        shiftRoutes,
        timelinePoints,
        (key, options) => t(Namespace.GpsTracking, key as any, options),
        "gps",
      ),
    [shiftRoutes, timelinePoints, t],
  );

  const tableSegments = useMemo(() => {
    if (timelineSource === "fact") return factSegments;
    if (timelineSource === "gps") return gpsSegments;
    return [...factSegments, ...gpsSegments].sort((a, b) => a.startTime - b.startTime);
  }, [timelineSource, factSegments, gpsSegments]);

  // When playback is active, filter GPS points to show only up to current time
  const displayGpsPoints = useMemo(() => {
    if (!isPlaybackActive || playbackTime === null) {
      return filteredGpsPoints;
    }

    // Show points up to current playback time
    return filteredGpsPoints.filter(
      (point: GpsTrackingPoint) => point.timestamp.getTime() <= playbackTime,
    );
  }, [filteredGpsPoints, playbackTime, isPlaybackActive]);

  // Calculate current vehicle position during playback
  const playbackPosition = useMemo(() => {
    if (!isPlaybackActive || timelinePoints.length === 0 || playbackTime === null) {
      return null;
    }

    return findPositionAtTime(timelinePoints, playbackTime);
  }, [playbackTime, timelinePoints, isPlaybackActive]);

  const handleTimeChange = useCallback((newTime: number) => {
    setPlaybackTime(newTime);
    setIsPlaybackActive(true);
  }, []);

  return (
    <div className="flex h-full w-full overflow-hidden gap-4">
      <div className="w-1/3 min-w-[320px] max-w-[400px] flex flex-col h-full rounded-2xl">
        <VehicleList
          vehicles={vehicles}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          startDate={startDate}
          endDate={endDate}
          setDateRange={setDateRange}
          selectedVehicleId={selectedVehicleId}
          onVehicleSelect={handleVehicleSelect}
          selectedShiftId={selectedShiftId}
          onShiftSelect={handleShiftSelect}
        />
      </div>

      <div className="flex-1 flex flex-col h-full min-w-0 overflow-hidden gap-4">
        <div className="flex-1 min-h-0">
          <MapViewSection
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            gpsPoints={displayGpsPoints}
            trackWindow={trackWindow}
            selectedVehicle={selectedVehicle}
            selectedPointId={selectedPointId}
            onPointClick={handlePointClick}
            pickupLocations={usedPickupLocations}
            stepPins={stepPins}
            depots={usedDepots}
            playbackPosition={playbackPosition}
            playbackTime={playbackTime}
            timelinePoints={timelinePoints}
            timelineSegments={factSegments}
            tableSegments={tableSegments}
            onTimeChange={handleTimeChange}
            isPlaybackActive={isPlaybackActive}
            hasVehicles={vehicles.length > 0}
            timelineSource={timelineSource}
            onTimelineSourceChange={setTimelineSource}
            initialPlaybackTime={initialPlaybackTime}
            photoPoints={photoPoints}
          />
        </div>
      </div>
    </div>
  );
}
